/*
 * YESOUL Saki · T5 Series 260728
 * 组件初始化可重复执行，兼容 Shopify Theme Editor 的局部重载。
 */
(() => {
  if (window.__ysSakiT5Runtime?.initScope) {
    window.__ysSakiT5Runtime.initScope(document);
    return;
  }

  document.documentElement.classList.add('ys-saki-js');
  document.getElementById('main')?.classList.add('ys-t5-series-polish');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const compactMovement = window.matchMedia('(max-width: 1023px)');
  const compactModels = window.matchMedia('(max-width: 767px)');
  const autoplayMedia = new Map();
  let autoplayObserver;
  let mediaRefreshFrame;

  const onMediaQueryChange = (query, callback) => {
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', callback);
    } else {
      query.addListener?.(callback);
    }
  };

  const mediaNodesIn = (scope, selector) => {
    const nodes = [];
    if (scope?.matches?.(selector)) nodes.push(scope);
    scope?.querySelectorAll?.(selector).forEach((node) => nodes.push(node));
    return nodes;
  };

  const isAutoplayMediaActive = (media, state) => {
    if (
      reducedMotion.matches
      || document.visibilityState === 'hidden'
      || !state.visible
      || !media.isConnected
      || !media.getClientRects().length
    ) {
      return false;
    }

    // Theme Editor、Tabs 与循环轮播会保留不可见节点；这些副本不得抢占播放。
    const hiddenAncestor = media.parentElement?.closest('[hidden], [aria-hidden="true"]');
    if (hiddenAncestor) return false;

    const peekSlide = media.closest('[data-ys-peek-slide]');
    if (peekSlide && !peekSlide.classList.contains('is-active')) return false;

    // Movement 在 PC 同屏展示时允许可见视频同时播放；移动端仅播放当前居中的卡片。
    const movementSlide = media.closest('[data-ys-movement-slide]');
    const movementViewport = movementSlide?.closest('[data-ys-movement-viewport]');
    if (
      movementSlide
      && movementViewport
      && compactMovement.matches
      && movementSlide.getAttribute('aria-current') !== 'true'
    ) {
      return false;
    }

    return true;
  };

  const pauseAutoplayMedia = (media) => {
    const video = media.querySelector('video');
    try {
      media.pause?.();
    } catch (_) {
      // 自定义元素尚未升级时，下面的原生 pause 仍可安全执行。
    }
    if (video && !video.paused) video.pause();
  };

  const playAutoplayMedia = (media) => {
    const video = media.querySelector('video');
    if (!video) return;

    // 移动浏览器自动播放的必要条件，同时维持 Impact 的原生播放状态管理。
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.removeAttribute('autoplay');

    if (!video.paused && !video.ended) return;
    try {
      media.play?.();
    } catch (_) {
      // Impact 的 VideoMedia 会自行处理 NotAllowedError 并回退到可手动播放状态。
    }
  };

  const reconcileAutoplayMedia = () => {
    autoplayMedia.forEach((state, media) => {
      if (!media.isConnected) {
        autoplayObserver?.unobserve(media);
        autoplayMedia.delete(media);
        return;
      }

      if (isAutoplayMediaActive(media, state)) {
        playAutoplayMedia(media);
      } else {
        pauseAutoplayMedia(media);
      }
    });
  };

  const scheduleMediaRefresh = () => {
    if (mediaRefreshFrame) return;
    mediaRefreshFrame = requestAnimationFrame(() => {
      mediaRefreshFrame = null;
      reconcileAutoplayMedia();
    });
  };

  if ('IntersectionObserver' in window) {
    autoplayObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const state = autoplayMedia.get(entry.target);
        if (!state) return;
        // 25% 可见阈值可避免移动端左右露出的提示卡片同时播放。
        state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
      });
      scheduleMediaRefresh();
    }, { threshold: [0, 0.25, 0.5, 0.75] });
  }

  const refreshAutoplayMedia = (scope = document) => {
    mediaNodesIn(scope, 'video-media[data-ys-autoplay-video]').forEach((media) => {
      if (autoplayMedia.has(media)) return;

      const state = { visible: !autoplayObserver };
      autoplayMedia.set(media, state);
      media.dataset.ysAutoplayReady = 'true';
      autoplayObserver?.observe(media);
    });

    scheduleMediaRefresh();
  };

  const teardownAutoplayMedia = (scope) => {
    mediaNodesIn(scope, 'video-media[data-ys-autoplay-ready]').forEach((media) => {
      pauseAutoplayMedia(media);
      autoplayObserver?.unobserve(media);
      autoplayMedia.delete(media);
      delete media.dataset.ysAutoplayReady;
    });
  };

  const initReveal = (scope) => {
    const items = scope.querySelectorAll('[data-ys-reveal]:not([data-ys-reveal-ready])');
    if (!items.length) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => {
        item.dataset.ysRevealReady = 'true';
        item.classList.add('is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver((entries, currentObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        currentObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    items.forEach((item) => {
      item.dataset.ysRevealReady = 'true';
      observer.observe(item);
    });
  };

  const initNavigation = (root) => {
    if (root.dataset.ysReady === 'true') return;
    root.dataset.ysReady = 'true';
    const bar = root.querySelector('[data-ys-nav-bar]');
    const progress = root.querySelector('[data-ys-progress]');
    const anchorLinks = [...root.querySelectorAll('[data-ys-nav-link]')];
    const sticky = root.dataset.ysSticky === 'true';
    if (!bar) return;

    // 跨组件读取仅用于计算 Impact Header 的实际底边，避免页面导航吸顶后遮挡主站 Header。
    const headerSelector = 'sticky-header, .header-wrapper, .header__wrapper, .shopify-section-header, .section-header, header[role="banner"], header';

    const headerOffset = () => {
      let bottom = 0;
      document.querySelectorAll(headerSelector).forEach((element) => {
        if (element === root || root.contains(element) || element.contains(root)) return;
        const rect = element.getBoundingClientRect();
        if (rect.height > 0 && rect.bottom > 0 && rect.top <= 1 && rect.bottom > bottom) {
          bottom = rect.bottom;
        }
      });
      const manualOffset = Number.parseInt(getComputedStyle(root).getPropertyValue('--ys-sticky-offset'), 10) || 0;
      return Math.round(bottom) + manualOffset;
    };

    const updateProgress = () => {
      if (!progress) return;
      const doc = document.documentElement;
      const available = Math.max(doc.scrollHeight - window.innerHeight, 1);
      progress.style.width = `${Math.min(100, Math.max(0, window.scrollY / available * 100))}%`;
    };

    const localAnchors = anchorLinks.map((link) => {
      let url;
      try {
        url = new URL(link.href, window.location.href);
      } catch (_error) {
        return null;
      }
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !url.hash) return null;
      const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      return target ? { link, target } : null;
    }).filter(Boolean);

    const setActiveLink = (activeLink) => {
      anchorLinks.forEach((link) => {
        if (link === activeLink) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    };

    const updateActiveLink = () => {
      if (!localAnchors.length || window.matchMedia('(max-width: 767px)').matches) return;
      const activationLine = headerOffset() + (bar.classList.contains('is-fixed') ? bar.getBoundingClientRect().height : 0) + 32;
      let current = localAnchors[0];
      localAnchors.forEach((entry) => {
        if (entry.target.getBoundingClientRect().top <= activationLine) current = entry;
      });
      setActiveLink(current.link);
    };

    localAnchors.forEach(({ link }) => {
      link.addEventListener('click', () => setActiveLink(link));
    });

    const updateSticky = () => {
      if (!sticky) {
        bar.classList.remove('is-pending');
        bar.removeAttribute('aria-hidden');
        return;
      }
      const line = headerOffset();
      const naturalTop = root.getBoundingClientRect().top;
      const fixed = bar.classList.contains('is-fixed');

      if (naturalTop <= line) {
        root.style.setProperty('--ys-nav-header-height', `${line}px`);
        if (!fixed) {
          bar.classList.remove('is-pending');
          bar.classList.add('is-fixed');
          bar.setAttribute('aria-hidden', 'false');
        }
      } else if (fixed) {
        bar.classList.remove('is-fixed');
        bar.classList.add('is-pending');
        bar.setAttribute('aria-hidden', 'true');
      }
    };

    let frame;
    const update = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        updateProgress();
        updateSticky();
        updateActiveLink();
      });
    };

    const resize = () => {
      bar.classList.remove('is-fixed');
      if (sticky) {
        bar.classList.add('is-pending');
        bar.setAttribute('aria-hidden', 'true');
      }
      updateProgress();
      updateSticky();
      updateActiveLink();
    };

    updateProgress();
    updateSticky();
    updateActiveLink();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', resize, { passive: true });
    window.setTimeout(resize, 300);
  };

  const initMovementCarousel = (root) => {
    if (root.dataset.ysMovementReady === 'true') return;
    root.dataset.ysMovementReady = 'true';

    const viewport = root.querySelector('[data-ys-movement-viewport]');
    const slides = [...root.querySelectorAll('[data-ys-movement-slide]')];
    const previous = root.querySelector('[data-ys-movement-prev]');
    const next = root.querySelector('[data-ys-movement-next]');
    const current = root.querySelector('[data-ys-movement-current]');
    if (!viewport || !slides.length) return;

    let index = 0;
    const canScroll = () => viewport.scrollWidth - viewport.clientWidth > 2;

    const paint = (nextIndex) => {
      index = Math.max(0, Math.min(nextIndex, slides.length - 1));
      if (current) current.textContent = String(index + 1);
      const scrollable = canScroll();
      if (previous) previous.disabled = !scrollable || index === 0;
      if (next) next.disabled = !scrollable || index === slides.length - 1;
      slides.forEach((slide, slideIndex) => {
        slide.setAttribute('aria-current', slideIndex === index ? 'true' : 'false');
      });
      scheduleMediaRefresh();
    };

    const nearestIndex = () => {
      const viewportCenter = viewport.scrollLeft + viewport.clientWidth / 2;
      let nearest = 0;
      let distance = Infinity;
      slides.forEach((slide, slideIndex) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const nextDistance = Math.abs(slideCenter - viewportCenter);
        if (nextDistance < distance) {
          nearest = slideIndex;
          distance = nextDistance;
        }
      });
      return nearest;
    };

    const goTo = (nextIndex) => {
      const targetIndex = Math.max(0, Math.min(nextIndex, slides.length - 1));
      const target = slides[targetIndex];
      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const desired = target.offsetLeft - (viewport.clientWidth - target.offsetWidth) / 2;
      viewport.scrollTo({
        left: Math.max(0, Math.min(desired, maxScroll)),
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
      paint(targetIndex);
    };

    previous?.addEventListener('click', () => goTo(index - 1));
    next?.addEventListener('click', () => goTo(index + 1));
    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(index - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(index + 1);
      }
    });

    let frame;
    viewport.addEventListener('scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        if (canScroll()) paint(nearestIndex());
      });
    }, { passive: true });

    let resizeTimer;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (canScroll()) {
          goTo(index);
        } else {
          viewport.scrollLeft = 0;
          paint(0);
        }
      }, 150);
    }, { passive: true });

    requestAnimationFrame(() => paint(0));
  };

  const initCarousel = (root) => {
    if (root.dataset.ysReady === 'true') return;
    root.dataset.ysReady = 'true';

    const track = root.querySelector('[data-ys-carousel-track]');
    const slides = [...root.querySelectorAll('[data-ys-carousel-slide]')];
    const previous = root.querySelector('[data-ys-carousel-prev]');
    const next = root.querySelector('[data-ys-carousel-next]');
    const current = root.querySelector('[data-ys-carousel-current]');
    const dots = root.querySelector('[data-ys-carousel-dots]');
    const loop = root.dataset.ysCarouselLoop === 'true';
    if (!track || !slides.length) return;

    let index = 0;

    const render = () => {
      track.style.transform = `translate3d(${-index * 100}%, 0, 0)`;
      slides.forEach((slide, slideIndex) => {
        slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
      });
      if (current) current.textContent = String(index + 1);
      if (previous) previous.disabled = !loop && index === 0;
      if (next) next.disabled = !loop && index === slides.length - 1;
      if (dots) {
        [...dots.children].forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === index);
          dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
        });
      }
      scheduleMediaRefresh();
    };

    const move = (direction) => {
      if (loop) {
        index = (index + direction + slides.length) % slides.length;
      } else {
        index = Math.min(slides.length - 1, Math.max(0, index + direction));
      }
      render();
    };

    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') move(-1);
      if (event.key === 'ArrowRight') move(1);
    });

    if (dots) {
      slides.forEach((_, slideIndex) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = `ys-saki-dot${slideIndex === 0 ? ' is-active' : ''}`;
        dot.setAttribute('aria-label', `Show slide ${slideIndex + 1}`);
        dot.addEventListener('click', () => {
          index = slideIndex;
          render();
        });
        dots.appendChild(dot);
      });
      dots.style.display = 'flex';
    }

    render();
  };

  const initPeekCarousel = (root) => {
    if (root.dataset.ysPeekReady === 'true') return;
    root.dataset.ysPeekReady = 'true';

    const track = root.querySelector('[data-ys-peek-track]');
    const realSlides = [...root.querySelectorAll('[data-ys-peek-slide]')];
    const dots = [...root.querySelectorAll('.ys-saki-hardware__dot')];
    const previous = root.querySelector('[data-ys-peek-prev]');
    const next = root.querySelector('[data-ys-peek-next]');
    if (!track || !realSlides.length) return;

    const count = realSlides.length;
    const loop = count > 1;
    let slides = realSlides;
    let offset = 0;
    let current = 0;
    let motionFrame = 0;
    let scrollFrame = 0;
    let settleTimer = 0;
    let resizeTimer = 0;
    let isAnimating = false;
    let suppressScroll = false;
    let queuedDirections = [];
    let pendingDotIndex = null;
    let restoreMotionStyles = null;

    if (loop) {
      const lastClone = realSlides[count - 1].cloneNode(true);
      const firstClone = realSlides[0].cloneNode(true);
      lastClone.removeAttribute('id');
      firstClone.removeAttribute('id');
      lastClone.setAttribute('aria-hidden', 'true');
      firstClone.setAttribute('aria-hidden', 'true');
      track.insertBefore(lastClone, realSlides[0]);
      track.appendChild(firstClone);
      slides = [lastClone, ...realSlides, firstClone];
      offset = 1;
      current = 1;
    }

    const centerOf = (element) => element.offsetLeft + element.offsetWidth / 2;
    const realIndexOf = (position) => {
      if (!loop) return position;
      if (position === 0) return count - 1;
      if (position === slides.length - 1) return 0;
      return position - 1;
    };

    const paint = (position, refreshMedia = true) => {
      slides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === position);
        if (!slide.hasAttribute('aria-hidden')) {
          slide.setAttribute('aria-current', index === position ? 'true' : 'false');
        }
      });
      const realIndex = realIndexOf(position);
      dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === realIndex);
        dot.setAttribute('aria-current', index === realIndex ? 'true' : 'false');
      });
      if (refreshMedia) scheduleMediaRefresh();
    };

    const leftOf = (position) => centerOf(slides[position]) - track.clientWidth / 2;

    const jumpTo = (position) => {
      const behavior = track.style.scrollBehavior;
      const snapType = track.style.scrollSnapType;
      suppressScroll = true;
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
      track.scrollLeft = leftOf(position);
      track.style.scrollBehavior = behavior;
      track.style.scrollSnapType = snapType;
      requestAnimationFrame(() => {
        suppressScroll = false;
      });
    };

    const nearestPosition = () => {
      const midpoint = track.scrollLeft + track.clientWidth / 2;
      let nearest = 0;
      let distance = Infinity;
      slides.forEach((slide, index) => {
        const nextDistance = Math.abs(centerOf(slide) - midpoint);
        if (nextDistance < distance) {
          nearest = index;
          distance = nextDistance;
        }
      });
      return nearest;
    };

    const normalizeLoopPosition = (position) => {
      if (!loop) return Math.min(slides.length - 1, Math.max(0, position));
      if (position <= 0) return count;
      if (position >= slides.length - 1) return offset;
      return position;
    };

    /*
     * 不依赖浏览器原生 smooth scroll 的结束时机。
     * 使用固定状态机逐步消费点击队列，动画真正结束后才执行首尾克隆卡归位，
     * 避免快速点击时 current 被滚动事件改写，或停止计时器过早触发跳转。
     */
    const animateTo = (position, onComplete) => {
      const startLeft = track.scrollLeft;
      const endLeft = leftOf(position);
      const distance = endLeft - startLeft;

      isAnimating = true;
      paint(position, false);

      if (reducedMotion.matches || Math.abs(distance) < 1) {
        jumpTo(position);
        requestAnimationFrame(() => onComplete(position));
        return;
      }

      const previousBehavior = track.style.scrollBehavior;
      const previousSnapType = track.style.scrollSnapType;
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
      restoreMotionStyles = () => {
        track.style.scrollBehavior = previousBehavior;
        track.style.scrollSnapType = previousSnapType;
        restoreMotionStyles = null;
      };

      const startedAt = performance.now();
      const duration = Math.min(360, Math.max(260, 230 + Math.abs(distance) * 0.08));
      const tick = (now) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        track.scrollLeft = startLeft + distance * eased;

        if (progress < 1) {
          motionFrame = requestAnimationFrame(tick);
          return;
        }

        motionFrame = 0;
        track.scrollLeft = endLeft;
        restoreMotionStyles?.();
        requestAnimationFrame(() => onComplete(position));
      };

      motionFrame = requestAnimationFrame(tick);
    };

    const finishAt = (position) => {
      let settledPosition = normalizeLoopPosition(position);
      if (settledPosition !== position) jumpTo(settledPosition);

      current = settledPosition;
      isAnimating = false;
      paint(current);

      requestAnimationFrame(() => {
        if (pendingDotIndex !== null) {
          const realIndex = pendingDotIndex;
          pendingDotIndex = null;
          goToRealIndex(realIndex);
          return;
        }
        runQueuedMove();
      });
    };

    const runQueuedMove = () => {
      if (isAnimating || pendingDotIndex !== null || !queuedDirections.length) return;
      const direction = queuedDirections.shift();
      const target = Math.min(slides.length - 1, Math.max(0, current + direction));
      if (target === current) {
        runQueuedMove();
        return;
      }
      animateTo(target, finishAt);
    };

    const move = (direction) => {
      const step = direction < 0 ? -1 : 1;
      const lastQueued = queuedDirections[queuedDirections.length - 1];

      // 尚未执行的相反方向互相抵消，避免用户快速修正方向时产生多余来回动画。
      if (lastQueued === -step) {
        queuedDirections.pop();
      } else {
        queuedDirections.push(step);
      }
      runQueuedMove();
    };

    const closestPhysicalPosition = (realIndex) => {
      const realPosition = realIndex + offset;
      if (!loop) return realPosition;

      return [realPosition, realPosition - count, realPosition + count]
        .filter((position) => position >= 0 && position < slides.length)
        .reduce((closest, position) => (
          Math.abs(position - current) < Math.abs(closest - current) ? position : closest
        ), realPosition);
    };

    function goToRealIndex(realIndex) {
      const safeIndex = Math.min(count - 1, Math.max(0, realIndex));
      queuedDirections = [];
      if (isAnimating) {
        pendingDotIndex = safeIndex;
        return;
      }

      const target = closestPhysicalPosition(safeIndex);
      if (target === current) {
        paint(current);
        return;
      }
      animateTo(target, finishAt);
    }

    const settleManualScroll = () => {
      if (isAnimating) return;
      const position = nearestPosition();
      const settledPosition = normalizeLoopPosition(position);
      if (settledPosition !== position) jumpTo(settledPosition);
      current = settledPosition;
      paint(current);
    };

    const interruptMotion = () => {
      if (!isAnimating) return;
      if (motionFrame) cancelAnimationFrame(motionFrame);
      motionFrame = 0;
      restoreMotionStyles?.();
      queuedDirections = [];
      pendingDotIndex = null;
      isAnimating = false;
      current = nearestPosition();
      settleManualScroll();
    };

    dots.forEach((dot, index) => dot.addEventListener('click', () => goToRealIndex(index)));
    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(1);
      }
    });

    track.addEventListener('pointerdown', interruptMotion, { passive: true });
    track.addEventListener('wheel', interruptMotion, { passive: true });
    track.addEventListener('scroll', () => {
      if (suppressScroll || isAnimating) return;

      if (!scrollFrame) {
        scrollFrame = requestAnimationFrame(() => {
          scrollFrame = 0;
          paint(nearestPosition(), false);
        });
      }
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settleManualScroll, 120);
    }, { passive: true });

    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        interruptMotion();
        jumpTo(current);
        paint(current);
      }, 150);
    }, { passive: true });

    requestAnimationFrame(() => {
      jumpTo(current);
      paint(current);
    });
  };

  const initStorySwitcher = (root) => {
    if (root.dataset.ysStoryReady === 'true') return;

    const tabs = [...root.querySelectorAll('[data-ys-story-tab]')];
    const panels = [...root.querySelectorAll('[data-ys-story-panel]')];
    const points = tabs.map((tab) => tab.closest('[data-ys-story-point]'));
    const previousButton = root.querySelector('[data-ys-story-previous]');
    const nextButton = root.querySelector('[data-ys-story-next]');
    if (tabs.length !== 3 || panels.length !== 3 || points.some((point) => !point)) return;

    root.dataset.ysStoryReady = 'true';
    tabs.forEach((tab) => {
      tab.disabled = false;
    });
    let activeIndex = 0;
    let transitioning = false;
    let queuedActivation = null;

    const nextFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));
    const waitWithTimeout = (promise, timeout = 600) => Promise.race([
      promise,
      new Promise((resolve) => window.setTimeout(resolve, timeout)),
    ]);

    /*
     * 三素材切换先准备下一帧，再进行双层交叉渐隐：
     * 图片完成解码、视频至少拥有可绘制帧后才替换，避免黑帧与“卡一下”的错觉。
     */
    const preparePanelMedia = (panel) => {
      const image = panel.querySelector('img');
      if (image) {
        image.loading = 'eager';
        if (image.complete) return image.decode?.().catch(() => undefined) || Promise.resolve();

        return waitWithTimeout(new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        }));
      }

      const video = panel.querySelector('video');
      if (!video) return Promise.resolve();
      video.preload = 'auto';
      if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return Promise.resolve();

      return waitWithTimeout(new Promise((resolve) => {
        video.addEventListener('loadeddata', resolve, { once: true });
        video.addEventListener('canplay', resolve, { once: true });
        video.addEventListener('error', resolve, { once: true });
      }));
    };

    const waitForFade = (panel) => {
      if (reducedMotion.matches) return Promise.resolve();

      return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          panel.removeEventListener('transitionend', onTransitionEnd);
          window.clearTimeout(fallbackTimer);
          resolve();
        };
        const onTransitionEnd = (event) => {
          if (event.target === panel && event.propertyName === 'opacity') finish();
        };
        const fallbackTimer = window.setTimeout(finish, 560);
        panel.addEventListener('transitionend', onTransitionEnd);
      });
    };

    const updateNavigation = (nextIndex, focus) => {
      tabs.forEach((tab, index) => {
        const active = index === nextIndex;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        points[index].classList.toggle('is-active', active);
        if (focus && active) tab.focus();
      });
    };

    const activate = async (requestedIndex, focus = false) => {
      const nextIndex = Math.max(0, Math.min(requestedIndex, tabs.length - 1));
      if (transitioning) {
        // 高频滚轮或连续点击时只保留最后一次意图，避免动画堆积和来回跳帧。
        queuedActivation = { index: nextIndex, focus };
        return;
      }
      if (nextIndex === activeIndex) {
        updateNavigation(activeIndex, focus);
        return;
      }

      transitioning = true;
      const outgoingPanel = panels[activeIndex];
      const incomingPanel = panels[nextIndex];

      incomingPanel.classList.add('is-preparing');
      incomingPanel.querySelectorAll('video-media').forEach(playAutoplayMedia);
      await preparePanelMedia(incomingPanel);

      updateNavigation(nextIndex, focus);
      activeIndex = nextIndex;

      // 先让浏览器绘制准备态，再启动透明度过渡，确保 transition 始终可触发。
      await nextFrame();
      incomingPanel.getBoundingClientRect();

      outgoingPanel.classList.add('is-leaving');
      outgoingPanel.classList.remove('is-active');
      outgoingPanel.setAttribute('aria-hidden', 'true');
      outgoingPanel.setAttribute('inert', '');

      incomingPanel.classList.remove('is-preparing');
      incomingPanel.classList.add('is-active');
      incomingPanel.setAttribute('aria-hidden', 'false');
      incomingPanel.removeAttribute('inert');

      await waitForFade(incomingPanel);

      outgoingPanel.classList.remove('is-leaving');
      outgoingPanel.querySelectorAll('video-media').forEach(pauseAutoplayMedia);
      panels.forEach((panel, index) => {
        if (index === activeIndex) return;
        panel.classList.remove('is-active', 'is-preparing', 'is-leaving');
        panel.setAttribute('aria-hidden', 'true');
        panel.setAttribute('inert', '');
      });

      transitioning = false;
      refreshAutoplayMedia(root);

      const queued = queuedActivation;
      queuedActivation = null;
      if (queued && (queued.index !== activeIndex || queued.focus)) {
        activate(queued.index, queued.focus);
      }
    };

    // 初始化时预热三份素材；不改变首屏可见状态，也不会强制播放隐藏视频。
    panels.forEach((panel) => {
      preparePanelMedia(panel);
    });

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(index));
      tab.addEventListener('keydown', (event) => {
        let target = index;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = tabs.length - 1;
        else return;

        event.preventDefault();
        activate(target, true);
      });
    });

    // Theme Editor 选中某个 Selling Point 时同步展示对应素材，方便后台验收。
    root.addEventListener('shopify:block:select', (event) => {
      const point = event.target?.closest?.('[data-ys-story-point]');
      const tab = point?.querySelector?.('[data-ys-story-tab]');
      const index = tabs.indexOf(tab);
      if (index >= 0) activate(index);
    });

    // PC 端沿用逐项滚轮浏览，但参考 Impact 原生滚动组件的节流方式：
    // 先累积滚动意图，再通过 rAF 合并触控板的高频事件，并让切换节奏与淡入动画同步。
    // 到达首尾后不拦截继续向外的滚动，确保页面不会形成滚动陷阱。
    const wheelThreshold = 52;
    let wheelDelta = 0;
    let wheelFrame = 0;
    let wheelIdleTimer = 0;

    const normalizeWheelDelta = (event) => {
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * 16;
      if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * window.innerHeight;
      return event.deltaY;
    };

    const flushWheel = () => {
      wheelFrame = 0;
      if (Math.abs(wheelDelta) < wheelThreshold) return;

      const direction = wheelDelta > 0 ? 1 : -1;
      const nextIndex = activeIndex + direction;
      wheelDelta = 0;
      if (nextIndex < 0 || nextIndex >= tabs.length) return;

      activate(nextIndex);
    };

    root.addEventListener('wheel', (event) => {
      if (
        compactModels.matches
        || event.ctrlKey
        || Math.abs(event.deltaY) <= Math.abs(event.deltaX)
      ) return;

      const normalizedDelta = normalizeWheelDelta(event);
      const direction = normalizedDelta > 0 ? 1 : -1;
      const nextIndex = activeIndex + direction;
      if (nextIndex < 0 || nextIndex >= tabs.length) return;

      event.preventDefault();
      if (wheelDelta !== 0 && Math.sign(wheelDelta) !== direction) wheelDelta = 0;
      wheelDelta = Math.max(-180, Math.min(180, wheelDelta + normalizedDelta));

      window.clearTimeout(wheelIdleTimer);
      wheelIdleTimer = window.setTimeout(() => {
        wheelDelta = 0;
      }, 200);

      if (!wheelFrame) wheelFrame = requestAnimationFrame(flushWheel);
    }, { passive: false });

    // 移动端沿用全页轮播的圆形左右控件，并在三份叠层素材间循环切换。
    previousButton?.addEventListener('click', () => {
      activate((activeIndex - 1 + tabs.length) % tabs.length);
    });
    nextButton?.addEventListener('click', () => {
      activate((activeIndex + 1) % tabs.length);
    });

    activate(activeIndex);
  };

  const initDimensionsV2 = (root) => {
    if (root.dataset.ysDimensionsV2Ready === 'true') return;

    const panels = [...root.querySelectorAll('[data-ys-dim-v2-panel]')];
    const sceneButtons = [...root.querySelectorAll('[data-ys-dim-v2-scene]')];
    const modeButtons = [...root.querySelectorAll('[data-ys-dim-v2-mode]')];
    // 只有一个可用房间时 Liquid 会隐藏场景切换器；位置切换仍需正常初始化。
    if (!panels.length || !modeButtons.length) return;

    root.dataset.ysDimensionsV2Ready = 'true';
    let activeScene = root.dataset.defaultScene || 'living';
    let activeMode = root.dataset.defaultMode || 'flat';

    const activate = (requestedScene, requestedMode) => {
      const exactPanel = panels.find((panel) => (
        panel.dataset.scene === requestedScene && panel.dataset.mode === requestedMode
      ));
      const fallbackPanel = panels.find((panel) => panel.dataset.scene === requestedScene) || panels[0];
      const activePanel = exactPanel || fallbackPanel;
      activeScene = activePanel.dataset.scene;
      activeMode = activePanel.dataset.mode;

      panels.forEach((panel) => {
        const active = panel === activePanel;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
        panel.toggleAttribute('inert', !active);
      });

      sceneButtons.forEach((button) => {
        const active = button.dataset.ysDimV2Scene === activeScene;
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
      });

      modeButtons.forEach((button) => {
        const active = button.dataset.ysDimV2Mode === activeMode;
        button.setAttribute('aria-pressed', String(active));
      });

      root.dataset.activeScene = activeScene;
      root.dataset.activeMode = activeMode;
      scheduleMediaRefresh();
    };

    const moveButtonFocus = (buttons, currentIndex, event) => {
      let targetIndex = currentIndex;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        targetIndex = (currentIndex + 1) % buttons.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        targetIndex = (currentIndex - 1 + buttons.length) % buttons.length;
      } else if (event.key === 'Home') {
        targetIndex = 0;
      } else if (event.key === 'End') {
        targetIndex = buttons.length - 1;
      } else {
        return null;
      }
      event.preventDefault();
      buttons[targetIndex].focus();
      return buttons[targetIndex];
    };

    sceneButtons.forEach((button, index) => {
      button.addEventListener('click', () => activate(button.dataset.ysDimV2Scene, activeMode));
      button.addEventListener('keydown', (event) => {
        const target = moveButtonFocus(sceneButtons, index, event);
        if (target) activate(target.dataset.ysDimV2Scene, activeMode);
      });
    });

    modeButtons.forEach((button) => {
      const group = button.closest('[data-ys-dim-v2-mode-group]');
      const groupButtons = [...(group?.querySelectorAll('[data-ys-dim-v2-mode]') || [])];
      const index = groupButtons.indexOf(button);
      button.addEventListener('click', () => activate(activeScene, button.dataset.ysDimV2Mode));
      button.addEventListener('keydown', (event) => {
        const target = moveButtonFocus(groupButtons, index, event);
        if (target) activate(activeScene, target.dataset.ysDimV2Mode);
      });
    });

    // Shopify Theme Editor 选中任一状态 Block 时，画布同步展示对应素材。
    root.addEventListener('shopify:block:select', (event) => {
      const panel = event.target?.closest?.('[data-ys-dim-v2-panel]');
      if (panel) activate(panel.dataset.scene, panel.dataset.mode);
    });

    activate(activeScene, activeMode);
  };

  const initTabs = (root) => {
    if (root.dataset.ysReady === 'true') return;
    root.dataset.ysReady = 'true';
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const panels = [...root.querySelectorAll('[role="tabpanel"]')];
    if (!tabs.length || !panels.length) return;

    const activate = (index, focus = false) => {
      tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (focus && active) tab.focus();
      });
      panels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      scheduleMediaRefresh();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(index));
      tab.addEventListener('keydown', (event) => {
        let target = index;
        if (event.key === 'ArrowRight') target = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') target = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = tabs.length - 1;
        else return;
        event.preventDefault();
        activate(target, true);
      });
    });

    activate(Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')));
  };

  const centerFeaturedModel = (track) => {
    const featured = track.querySelector('.ys-saki-model.is-featured');
    if (!featured) return;

    if (!compactModels.matches) {
      track.scrollLeft = 0;
      return;
    }

    const desired = featured.offsetLeft - (track.clientWidth - featured.offsetWidth) / 2;
    const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
    track.scrollLeft = Math.max(0, Math.min(desired, maximum));
  };

  const refreshFeaturedModels = () => {
    requestAnimationFrame(() => {
      document
        .querySelectorAll('[data-ys-models][data-ys-models-ready="true"]')
        .forEach(centerFeaturedModel);
    });
  };

  const initModels = (track) => {
    if (track.dataset.ysModelsReady === 'true') return;
    track.dataset.ysModelsReady = 'true';

    requestAnimationFrame(() => centerFeaturedModel(track));
    window.setTimeout(() => centerFeaturedModel(track), 180);
  };

  const initFaq = (root) => {
    if (root.dataset.ysReady === 'true') return;
    root.dataset.ysReady = 'true';
    const singleOpen = root.dataset.ysSingleOpen === 'true';
    if (!singleOpen) return;
    root.querySelectorAll('details').forEach((detail) => {
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;
        root.querySelectorAll('details').forEach((other) => {
          if (other !== detail) other.open = false;
        });
      });
    });
  };

  const initConfidenceCards = (root) => {
    if (root.dataset.ysConfidenceReady === 'true') return;
    root.dataset.ysConfidenceReady = 'true';

    const cards = [...root.querySelectorAll('[data-ys-confidence-card]')];
    const track = root.querySelector('[data-ys-confidence-track]');
    const dots = [...root.querySelectorAll('[data-ys-confidence-dot]')];
    let activeIndex = 0;
    let scrollFrame;

    const setExpanded = (card, expanded) => {
      card.classList.toggle('is-expanded', expanded);
      card.querySelectorAll('[data-ys-confidence-toggle]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', String(expanded));
      });
    };

    const setActiveCard = (index) => {
      activeIndex = Math.max(0, Math.min(index, cards.length - 1));
      cards.forEach((card, cardIndex) => {
        const isActive = cardIndex === activeIndex;
        card.classList.toggle('is-current', isActive);
        card.setAttribute('aria-current', String(isActive));
      });
      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', String(isActive));
      });
    };

    const findCenteredCard = () => {
      if (!track || !cards.length) return 0;
      const viewportCenter = track.scrollLeft + (track.clientWidth / 2);
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + (card.offsetWidth / 2);
        const distance = Math.abs(cardCenter - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      return closestIndex;
    };

    const scrollToCard = (index) => {
      const card = cards[index];
      if (!track || !card) return;
      track.scrollTo({
        left: card.offsetLeft - ((track.clientWidth - card.offsetWidth) / 2),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
      setActiveCard(index);
    };

    cards.forEach((card) => {
      card.querySelectorAll('[data-ys-confidence-toggle]:not(:disabled)').forEach((toggle) => {
        toggle.addEventListener('click', () => {
          const willExpand = !card.classList.contains('is-expanded');

          // 触控设备一次仅展开一张，避免 Masonry 页面被多个长面板同时拉开。
          if (compactModels.matches || window.matchMedia('(hover: none), (pointer: coarse)').matches) {
            cards.forEach((otherCard) => {
              if (otherCard !== card) setExpanded(otherCard, false);
            });
          }

          setExpanded(card, willExpand);
        });
      });
    });

    if (track && cards.length) {
      setActiveCard(findCenteredCard());

      // 原生滚动负责触摸手势；rAF 只同步居中卡片与圆点，避免高频 scroll 抖动。
      track.addEventListener('scroll', () => {
        if (scrollFrame) return;
        scrollFrame = requestAnimationFrame(() => {
          scrollFrame = null;
          setActiveCard(findCenteredCard());
        });
      }, { passive: true });

      track.addEventListener('keydown', (event) => {
        if (!compactModels.matches || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
        event.preventDefault();
        scrollToCard(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
      });

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => scrollToCard(index));
      });
    }
  };

  const initScope = (scope = document) => {
    initReveal(scope);
    scope.querySelectorAll('[data-ys-navigation]').forEach(initNavigation);
    scope.querySelectorAll('[data-ys-movement-carousel]').forEach(initMovementCarousel);
    scope.querySelectorAll('[data-ys-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-ys-peek-carousel]').forEach(initPeekCarousel);
    scope.querySelectorAll('[data-ys-story-switcher]').forEach(initStorySwitcher);
    scope.querySelectorAll('[data-ys-dimensions-v2]').forEach(initDimensionsV2);
    scope.querySelectorAll('[data-ys-tabs]').forEach(initTabs);
    scope.querySelectorAll('[data-ys-models]').forEach(initModels);
    scope.querySelectorAll('[data-ys-faq]').forEach(initFaq);
    scope.querySelectorAll('[data-ys-confidence]').forEach(initConfidenceCards);
    refreshAutoplayMedia(scope);
  };

  window.__ysSakiT5Runtime = { initScope, refreshAutoplayMedia };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initScope());
  } else {
    initScope();
  }

  document.addEventListener('shopify:section:load', (event) => initScope(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardownAutoplayMedia(event.target));
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      autoplayMedia.forEach((_, media) => pauseAutoplayMedia(media));
    } else {
      scheduleMediaRefresh();
    }
  });
  window.addEventListener('resize', scheduleMediaRefresh, { passive: true });
  window.addEventListener('pageshow', scheduleMediaRefresh);
  window.addEventListener('pagehide', () => autoplayMedia.forEach((_, media) => pauseAutoplayMedia(media)));
  onMediaQueryChange(reducedMotion, scheduleMediaRefresh);
  onMediaQueryChange(compactMovement, scheduleMediaRefresh);
  onMediaQueryChange(compactModels, refreshFeaturedModels);
  if (window.customElements?.whenDefined) {
    window.customElements.whenDefined('video-media').then(scheduleMediaRefresh);
  }
})();
